import React, { useState, useEffect, useMemo } from 'react';
import { Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, Alert, } from 'reactstrap';

// Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

//redux
import { setRequireCloseModal } from '../../../../slices/feedingBarges/reducer';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { addNewFeedingBarges, updateFeedingBarges } from '../../../../slices/feedingBarges/thunk';
import FeedingSiloList from '../../FeedingSilo/FeedingSiloList';
import { addNewBulkNotification, getFeedingBargesList, getFeedingSiloList, getFuelTankList, getPowerGenerationSourceList, getUserByRoles, sendNewEmailMessage } from '../../../../slices/thunks';
import { ODataGrid } from '../../../../Components/OElements/ODataGrid';
import OSelect from '../../../../Components/OElements/OSelect';
import { getEstablishmentList, getFeedingBargesTypeList, getProductiveCycleList, getPropertyTypeList } from '../../../../helpers/establishment/backend_helper';
import { useUser } from '../../../../Components/Hooks/useUserHooks';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import OInput from '../../../../Components/OElements/OInput';

const FeedingBargesForm = ({ dataFeedingBarges, isEdit, handleToggleModal, isOpen, activeTab, toggleTab, }) => {
	const dispatch = useDispatch();
	const { user } = useUser();

	const selectorFeedingBarges = createSelector(
		state => state.FeedingBarges,
		holding => ({
			isAdding: holding.isAdding,
			isAdded: holding.isAdded,
			isUpdating: holding.isUpdating,
			isUpdated: holding.isUpdated,
			isLoadingList: holding.isLoadingList,
			requireCloseModal: holding.requireCloseModal,
		})
	);
	const {
		isAdding,
		isAdded,
		isUpdating,
		isUpdated,
		isLoadingList,
		requireCloseModal,
	} = useSelector(selectorFeedingBarges);

	const [selectedEstablishment, setSelectedEstablishment] = useState('loading');
	const handleEstablishmentSelect = (selected) => {
		setSelectedEstablishment(selected);
	};



	const selectorUser = createSelector(
		state => state.Roles,
		result => ({
			users: result.itemUserRoleList,
		})
	);
	const { users } = useSelector(selectorUser);

	useEffect(() => {
		const roles = ['ADM', 'MAB', 'OPE', 'MNT'];
		const params = { roles: roles.join(',') };

		params['establishment_id'] = selectedEstablishment ? selectedEstablishment._id : "";

		dispatch(getUserByRoles(params));
	}, [selectedEstablishment]);


	const sendNotificationAndEmail = (values, isEdit, previousEstablishment) => {
		const establishmentName = selectedEstablishment?.name?.trim().toUpperCase() || '';
		const previousEstablishmentName = previousEstablishment?.name?.trim().toUpperCase() || '';
		const artifactName = values.name?.trim().toUpperCase() || '';
		const userName = user?.full_name?.trim().toUpperCase() || 'Usuario no identificado';

		const uniqueEmails = [...new Set(users.filter(user => user.email).map(user => user.email))].join(',');
		const user_ids = [...new Set(users.filter(user => user._id).map(user => user._id))];

		const sendEmailAndNotification = (subject, body, message) => {
			dispatch(sendNewEmailMessage({ to_email: uniqueEmails, subject, body }));
			dispatch(addNewBulkNotification({ title: subject, message, notification_priority_code: 'M', notification_type_code: 'INF', user_ids }));
		};

		if (!isEdit && selectedEstablishment) {
			// Nueva asociación
			const subject = `Nueva Asociación de Artefacto Naval`;
			const body = `
            <div style="font-family: Arial, sans-serif; line-height: 1.6;">
                <h2>${subject}</h2>
                <p>Se ha registrado un artefacto naval asociado al establecimiento "${establishmentName}".</p>
                <p>Registrado por: ${userName}</p>
            </div>`;
			const message = `El artefacto naval "${artifactName}" ha sido registrado y asociado al establecimiento "${establishmentName}".`;

			sendEmailAndNotification(subject, body, message);
		} else if (isEdit) {
			if (selectedEstablishment && previousEstablishment?._id !== selectedEstablishment?._id) {
				// Cambio de asociación
				const action = previousEstablishment ? 'cambiado' : 'asociado';
				const subject = `Actualización de Asociación de Artefacto Naval`;

				const body = `<div style="font-family: Arial, sans-serif; line-height: 1.6;">
												<h2>${subject}</h2>
												<p>El artefacto naval "${artifactName}" ha sido ${action} ${previousEstablishment ? `del establecimiento "${previousEstablishmentName}" ` : ''}al establecimiento "${establishmentName}".</p>
												<p>Modificado por: ${userName}</p>
										</div>`;

				const message = `El artefacto naval "${artifactName}" ha sido ${action} ${previousEstablishment ? `del establecimiento "${previousEstablishmentName}" ` : ''}al establecimiento "${establishmentName}".`;


				sendEmailAndNotification(subject, body, message);
			} else if (previousEstablishment && !selectedEstablishment) {
				// Desasociación
				const subject = `Desasociación de Artefacto Naval`;
				const body = `
                <div style="font-family: Arial, sans-serif; line-height: 1.6;">
                    <h2>${subject}</h2>
                    <p>El artefacto naval "${artifactName}" ha sido desasociado del establecimiento "${previousEstablishmentName}" y se encuentra disponible.</p>
                    <p>Modificado por: ${userName}</p>
                </div>`;
				const message = `El artefacto naval "${artifactName}" ha sido desasociado del establecimiento "${previousEstablishmentName}" y se encuentra disponible.`;

				sendEmailAndNotification(subject, body, message);
			}
		}
	};


	// validation
	const validation = useFormik({
		// enableReinitialize: use this flag when initial values needs to be changed
		enableReinitialize: true,
		initialValues: {
			name: (dataFeedingBarges && dataFeedingBarges.name) || '',
			serial_number: (dataFeedingBarges && dataFeedingBarges.serial_number) || '',
			registration_number: (dataFeedingBarges && dataFeedingBarges.registration_number) || '',
			property_type_id: (dataFeedingBarges && dataFeedingBarges.property_type_id) || '',
			feeding_barges_type_id: (dataFeedingBarges && dataFeedingBarges.feeding_barges_type_id) || '',
			establishment_id: (dataFeedingBarges && dataFeedingBarges.establishment_id) || '',
			description: (dataFeedingBarges && dataFeedingBarges.description) || '',
			assignation_date: (dataFeedingBarges && dataFeedingBarges.assignation_date) || '',
			disabled: (dataFeedingBarges && dataFeedingBarges.disabled) || false,
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Campo requerido'),
			serial_number: Yup.string().required('Campo requerido'),
			registration_number: Yup.string().required('Campo requerido'),
			property_type_id: Yup.string().required('Campo requerido'),
			feeding_barges_type_id: Yup.string().required('Campo requerido'),
			establishment_id: Yup.string().nullable(),
			description: Yup.string(),
			assignation_date: Yup.lazy(() =>
				validation.values.establishment_id != null ?
					Yup.string().required('Campo requerido')
					: Yup.string().nullable()
			),
			// assignation_date: Yup.string()
			// 	.nullable()
			// 	.test('check-require-date-assignation', 'Campo requerido', value => {
			// 		if (
			// 			dataFeedingBarges?.establishment?._id !=
			// 			validation.values.establishment_id &&
			// 			!value
			// 		) {
			// 			return false;
			// 		}
			// 		return true;
			// 	}),
		}),
		onSubmit: values => {
			const previousEstablishment = dataFeedingBarges?.establishment
				? { _id: dataFeedingBarges.establishment._id, name: dataFeedingBarges.establishment.name }
				: null;

			const feedingBargesData = {
				name: values.name,
				serial_number: values.serial_number,
				registration_number: values.registration_number,
				property_type_id: values.property_type_id,
				feeding_barges_type_id: values.feeding_barges_type_id,
				establishment_id: values.establishment_id,
				description: values.description,
				disabled: values.disabled,
				assignation_date: values.establishment_id ? values.assignation_date : null
			};

			if (isEdit) {
				dispatch(updateFeedingBarges({ ...dataFeedingBarges, ...feedingBargesData }));
			} else {
				dispatch(addNewFeedingBarges(feedingBargesData));
			}

			dispatch(getFeedingBargesList());
			sendNotificationAndEmail(values, isEdit, previousEstablishment);
		}
	});

	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal]);


	const selectorFeedingSilo = createSelector(
		state => state.FeedingSilo,
		data => ({
			feedingSiloList: data.feedingSiloList,
			feedingSiloIsLoadingList: data.isLoadingList,
		})
	);

	const { feedingSiloList, feedingSiloIsLoadingList } = useSelector(selectorFeedingSilo);

	useEffect(() => {
		dispatch(getFeedingSiloList({ 'feeding_barges_id': dataFeedingBarges._id }));
	}, []);

	const columnsFeedingSilo = useMemo(
		() => [
			{ header: 'Nombre', accessorKey: 'name', },
			{ header: 'Capacidad', accessorKey: 'capacity', },
			{ header: 'Saldo', accessorKey: 'stock', },
			{ header: 'Alimento', accessorKey: 'material', },
			{ header: 'Descripción', accessorKey: 'description', },
		]
	);

	const selectorFuelTank = createSelector(
		state => state.FuelTank,
		data => ({
			fuelTankList: data.fuelTankList,
			fuelTankIsLoadingList: data.isLoadingList,
		})
	);

	const { fuelTankList, fuelTankIsLoadingList } = useSelector(selectorFuelTank);

	useEffect(() => {
		dispatch(getFuelTankList({ 'feeding_barges_id': dataFeedingBarges._id }));
	}, []);

	const columnsFuelTank = useMemo(
		() => [
			{ header: 'Nombre', accessorKey: 'name', },
			{ header: 'Capacidad', accessorKey: 'capacity', },
			{ header: 'Saldo', accessorKey: 'stock', },
			{ header: 'Tipo de combustible', accessorKey: 'fuel_type.name', },
			{ header: 'Descripción', accessorKey: 'description', },
		],
	);

	const selectorPowerGenerationSource = createSelector(
		state => state.PowerGenerationSource,
		data => ({
			powerGenerationSourceList: data.powerGenerationSourceList,
			powerGenerationSourceIsLoadingList: data.isLoadingList,
		})
	);

	const { powerGenerationSourceList, powerGenerationSourceIsLoadingList } = useSelector(selectorPowerGenerationSource);

	useEffect(() => {
		dispatch(getPowerGenerationSourceList({ 'feeding_barges_id': dataFeedingBarges._id }));
	}, []);

	// useEffect(() => {
	// 	if (dataFeedingBarges?.establishment?._id != validation.values.establishment_id) {
	// 		validation.setFieldValue('assignation_date', null);
	// 	}
	// }, [dataFeedingBarges, validation.values.establishment_id])

	const columnsPowerGenerationSource = useMemo(
		() => [
			{ header: 'Nombre', accessorKey: 'name', },
			{ header: 'Número de Serie', accessorKey: 'serial_number', size: 'auto' },
			{ header: 'Cod. RFP', accessorKey: 'rfp_code', size: 'auto' },
			{ header: 'Potencia (kW)', accessorKey: 'power_output', },
			{ header: 'Tipo de combustible', accessorKey: 'fuel_type.name', },
			{ header: 'Tipo', accessorKey: 'power_generation_type.name', },
			{ header: 'Estado', accessorKey: 'power_generation_status.name', },
			{ header: 'Descripción', accessorKey: 'description', },
		],
	);


	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) { toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 }); }
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<TabContent activeTab={activeTab}>
					<TabPane tabId='1'>
						<Row className='g-3'>
							<Col lg={3}>
								<OInput
									name='name'
									label='Nombre del Artefacto Naval'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='serial_number'
									label='Número de Serie'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='registration_number'
									label='Número de Matricula'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key={'feeding_barges_type_id'}
									label="Tipo"
									id="feeding_barges_type_id"
									name="feeding_barges_type_id"
									isRequired={true}
									value={(
										dataFeedingBarges?.feeding_barges_type?._id || ''
									)}
									requestApi={() => getFeedingBargesTypeList()}
									validation={validation}
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key={'property_type_id'}
									label="Tipo de Propiedad"
									id="property_type_id"
									name="property_type_id"
									isRequired={true}
									value={(
										dataFeedingBarges?.property_type?._id || ''
									)}
									requestApi={() => getPropertyTypeList()}
									validation={validation}
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key={'establishment_id'}
									label="Establecimiento"
									id="establishment_id"
									name="establishment_id"
									value={(
										dataFeedingBarges?.establishment_id || ''
									)}
									onSelect={handleEstablishmentSelect}
									requestApi={() => getEstablishmentList({ 'barges_needed': true })}
									validation={validation}
								/>
							</Col>
							{
								(
									(validation.values.establishment_id)
										?
										(
											<Col sm={3}>
												<DatesProvider
													settings={{
														locale: 'es-mx',
														firstDayOfWeek: 1,
														weekendDays: [0],
														timezone: 'UTC',
													}}
												>
													<DatePickerInput
														label={
															<span>
																Fecha de Asignación{" "}
																{dataFeedingBarges?.establishment?._id !== validation.values.establishment_id && (
																	<span style={{ color: "red" }}>*</span>
																)}
															</span>

														}
														id='assignation_date'
														name='assignation_date'
														placeholder='Seleccione una fecha'
														value={
															validation.values.assignation_date
																? new Date(validation.values.assignation_date)
																: null
														}
														onChange={date => {
															validation.setFieldValue(
																'assignation_date',
																date || null
															);
														}}
														onBlur={validation.handleBlur}
														maxDate={new Date()}
														valueFormat='DD-MM-YYYY'
														error={
															validation.touched.assignation_date &&
																validation.errors.assignation_date
																? validation.errors.assignation_date
																: ''
														}
														clearable
													/>
												</DatesProvider>
											</Col>
										)
										: null
								)
							}
							<Col lg={12}>
								<Label for='description' className='form-label'>
									Descripción
								</Label>
								<Input
									name='description'
									id='description'
									className='form-control'
									placeholder='Ingrese una descipción'
									type='textarea'
									validate={{ required: { value: true } }}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									value={validation.values.description || ''}
									invalid={
										validation.touched.description &&
											validation.errors.description
											? true
											: false
									}
								/>
								{
									validation.touched.description &&
										validation.errors.description ? (
										<FormFeedback type='invalid'>
											{validation.errors.description}
										</FormFeedback>
									) : null
								}
							</Col>
						</Row>
					</TabPane>
					<TabPane tabId='2'>
						{feedingSiloIsLoadingList ? (
							<Alert color='primary'>
								<Spinner size='sm' className='me-2' />
								Cargando datos, por favor espere...
							</Alert>
						) : null}
						<ODataGrid columns={columnsFeedingSilo} data={feedingSiloList || []} />
					</TabPane>
					<TabPane tabId='3'>
						{fuelTankIsLoadingList ? (
							<Alert color='primary'>
								<Spinner size='sm' className='me-2' />
								Cargando datos, por favor espere...
							</Alert>
						) : null}
						<ODataGrid columns={columnsFuelTank} data={fuelTankList || []} />
					</TabPane>
					<TabPane tabId='4'>
						{powerGenerationSourceIsLoadingList ? (
							<Alert color='primary'>
								<Spinner size='sm' className='me-2' />
								Cargando datos, por favor espere...
							</Alert>
						) : null}
						<ODataGrid columns={columnsPowerGenerationSource} data={powerGenerationSourceList || []} />
					</TabPane>
				</TabContent>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 modal-footer-add-btn'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}} className='btn-danger btn-label'>
						<i className="icon-x label-icon"></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={isAdding || isUpdating}
					>
						{isAdding || isUpdating ? (<i className='label-icon'><Spinner size='sm' /></i>) : (<i className='ri-save-3-line label-icon'></i>)}
						{isEdit ? 'Editar' : 'Crear'} Artefacto Naval
					</button>
				</div>
			</div>
		</Form>
	);
};

export default FeedingBargesForm;
